<template>
  <div class="otp-input-container">
    <template v-for="(input, index) in numInputs" :key="index">
      <input
        v-model="otp[index]"
        :class="[inputClasses, { 'otp-input-focused': activeInput === index }]"
        :type="inputType"
        maxlength="1"
        @input="handleInput(index)"
        @keydown="handleKeyDown($event, index)"
        @paste="handlePaste"
        @focus="activeInput = index"
        ref="inputs"
      />
      <!-- Add separator after each input except the last one -->
      <span v-if="separator && index < numInputs - 1" class="otp-separator">{{ separator }}</span>
    </template>
  </div>
</template>

<script>
export default {
  name: 'OtpInput',
  props: {
    numInputs: {
      type: Number,
      default: 4
    },
    separator: {
      type: String,
      default: ''
    },
    inputType: {
      type: String,
      default: 'text'
    },
    inputClasses: {
      type: String,
      default: 'otp-input'
    },
    shouldAutoFocus: {
      type: Boolean,
      default: true
    }
  },
  data () {
    return {
      otp: Array(this.numInputs).fill(''),
      activeInput: 0
    }
  },
  mounted () {
    if (this.shouldAutoFocus) {
      this.$nextTick(() => {
        this.focusInput(0)
      })
    }
  },
  methods: {
    focusInput (index) {
      if (index < 0 || index >= this.numInputs) return
      this.$refs.inputs[index].focus()
    },
    handleInput (index) {
      const value = this.otp[index]
      // Ensure only one character is entered
      if (value.length > 1) {
        this.otp[index] = value.slice(0, 1)
      }

      // Auto-focus next input
      if (value && index < this.numInputs - 1) {
        this.focusInput(index + 1)
      }

      this.emitChanges()
    },
    handleKeyDown (event, index) {
      const key = event.key

      // Handle backspace
      if (key === 'Backspace') {
        if (!this.otp[index] && index > 0) {
          // If current input is empty and backspace pressed, focus previous input
          this.focusInput(index - 1)
          event.preventDefault()
        }
      } else if (key === 'ArrowLeft' && index > 0) {
        this.focusInput(index - 1)
        event.preventDefault()
      } else if (key === 'ArrowRight' && index < this.numInputs - 1) {
        this.focusInput(index + 1)
        event.preventDefault()
      }
    },
    handlePaste (event) {
      event.preventDefault()
      const pastedData = event.clipboardData.getData('text')
      const otpLength = Math.min(pastedData.length, this.numInputs)

      for (let i = 0; i < otpLength; i++) {
        this.otp[i] = pastedData[i]
      }

      // Focus the input after the last pasted character
      if (otpLength < this.numInputs) {
        this.focusInput(otpLength)
      }

      this.emitChanges()
    },
    emitChanges () {
      const otpValue = this.otp.join('')
      this.$emit('on-change', otpValue)

      // Check if OTP is complete
      if (otpValue.length === this.numInputs) {
        this.$emit('on-complete', otpValue)
      }
    }
  }
}
</script>

<style scoped>
.otp-input-container {
  display: flex;
  align-items: center;
  justify-content: center;
}

.otp-input {
  width: 40px;
  height: 40px;
  margin: 0 4px;
  font-size: 20px;
  text-align: center;
  border: 1px solid #ccc;
  border-radius: 4px;
  transition: border-color 0.2s;
}

.otp-input:focus, .otp-input-focused {
  outline: none;
  border-color: #007bff;
  box-shadow: 0 0 0 0.2rem rgba(0,123,255,.25);
}

.otp-separator {
  margin: 0 4px;
  font-size: 20px;
  color: #999;
}
</style>